import { BaseText, Button, Meta, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePaymentStore } from 'stores/paymentStore';
import styled from 'styled-components/native';
import { StyledWrapper } from 'styles/ContainerStyles';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

const InfoRow = styled.View`
  flex-direction: row;
  align-items: center;
  padding-bottom: 16px;
  padding-top: 16px;
`;

const IconWrapper = styled.View`
  margin-right: 12px;
`;

const TextWrapper = styled.View`
  flex-shrink: 1;
  align-self: center;
`;

const Container = styled.View`
  flex: 1;
  flex-direction: column;
  flex-basis: auto;
`;

const Grow = styled.View`
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: auto;
`;

const Footer = styled.View`
  flex-direction: column;
  justify-content: flex-end;
  flex-shrink: 0;
`;

type CorporateAccountContentProps = {
  handleDone?: () => void;
};

const CorporateAccountContent = ({
  handleDone,
}: CorporateAccountContentProps) => {
  const { t } = useTranslation();
  const corporateAccount = usePaymentStore(
    (s) => s.selectedCorporateAccountPaymentOption
  );

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const getRegistrationPlatesText = () => {
    if (
      !corporateAccount.registrationPlates ||
      corporateAccount.registrationPlates.length === 0
    ) {
      return t('paymentMethods.anyVehicle');
    }

    return corporateAccount.registrationPlates
      .map((plate) => plate.plateText ?? '')
      .filter(Boolean)
      .join(', ');
  };

  const handleClose = () => {
    if (handleDone) {
      handleDone();
    } else {
      useUiStore.getState().setSheetModal('');
    }
  };

  return (
    <Container>
      <Grow>
        <StyledWrapper mb={20}>
          <InfoRow>
            <IconWrapper>
              <Icon
                name="Business-Filled"
                size="m"
                color={theme.colors.gray300}
              />
            </IconWrapper>
            <TextWrapper>
              <BaseText {...testIdentifiers('corporate-account-name-label')}>
                Name
              </BaseText>
              <Meta
                color={theme.colors.gray300}
                {...testIdentifiers('corporate-account-name-value')}>
                {corporateAccount.optionName ?? '-'}
              </Meta>
            </TextWrapper>
          </InfoRow>

          <InfoRow>
            <IconWrapper>
              <Icon
                name="Calendar-Outlined"
                size="m"
                color={theme.colors.gray300}
              />
            </IconWrapper>
            <TextWrapper>
              <BaseText
                {...testIdentifiers('corporate-account-valid-from-label')}>
                {t('paymentMethods.validFrom')}
              </BaseText>
              <Meta
                color={theme.colors.gray300}
                {...testIdentifiers('corporate-account-valid-from-value')}>
                {formatDate(corporateAccount.validFrom)}
              </Meta>
            </TextWrapper>
          </InfoRow>

          <InfoRow>
            <IconWrapper>
              <Icon
                name="Calendar-Outlined"
                size="m"
                color={theme.colors.gray300}
              />
            </IconWrapper>
            <TextWrapper>
              <BaseText
                {...testIdentifiers('corporate-account-valid-to-label')}>
                {t('paymentMethods.validTo')}
              </BaseText>
              <Meta
                color={theme.colors.gray300}
                {...testIdentifiers('corporate-account-valid-to-value')}>
                {formatDate(corporateAccount.validTo)}
              </Meta>
            </TextWrapper>
          </InfoRow>

          <InfoRow>
            <IconWrapper>
              <Icon name="Car-Outlined" size="m" color={theme.colors.gray300} />
            </IconWrapper>
            <TextWrapper>
              <BaseText
                {...testIdentifiers('corporate-account-vehicles-label')}>
                {t('paymentMethods.vehicles')}
              </BaseText>
              <Meta
                color={theme.colors.gray300}
                {...testIdentifiers('corporate-account-vehicles-value')}>
                {getRegistrationPlatesText()}
              </Meta>
            </TextWrapper>
          </InfoRow>
        </StyledWrapper>
      </Grow>

      <Footer>
        <StyledWrapper>
          <Button
            size="l"
            onPress={handleClose}
            text={t('button.close')}
            testID="corporate-account-close-button"
          />
        </StyledWrapper>
      </Footer>
    </Container>
  );
};

export default CorporateAccountContent;
